import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function ChangePassword() {
    const { user, refreshMe, logout } = useAuth();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
        if (password !== confirm) { setError("Passwords do not match."); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", { new_password: password });
            await refreshMe();
            toast.success("Password updated");
            navigate("/", { replace: true });
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to change password.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-ink-50 flex items-center justify-center p-6" data-testid="change-password-page">
            <div className="w-full max-w-md bg-white border border-ink-300 p-10">
                <Logo tone="light" className="h-7 mb-8" showSub />
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">First-time sign-in</div>
                <h2 className="font-editorial text-3xl text-ink-900 mb-2">Set your password</h2>
                <p className="text-sm text-ink-500 mb-8">
                    Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Before you continue, please replace your temporary password with something only you know.
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">New password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            data-testid="new-password-input"
                            className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                            placeholder="At least 8 characters"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Confirm new password</label>
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            minLength={8}
                            data-testid="confirm-password-input"
                            className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                            placeholder="Repeat"
                        />
                    </div>
                    {error && (
                        <div className="text-xs text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="change-password-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="change-password-submit-btn"
                        className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50"
                    >
                        {busy ? "Saving…" : "Set password & continue"}
                    </button>
                </form>
                <div className="mt-6 pt-6 border-t border-ink-200 text-center">
                    <button
                        type="button"
                        onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                        data-testid="change-password-logout-btn"
                        className="text-[11px] uppercase tracking-[0.15em] text-ink-500 hover:text-haevn-700 font-mono"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}
